import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { fetchJson } from "@/lib/api";

// Phase 2a customer-accounts /account SPA — read + write hooks. Each
// mutation invalidates the relevant query so the tabs stay in sync
// without manual cache pokes.

export interface AccountProfile {
  first_name: string;
  last_name: string;
  phone: string;
  company_name: string | null;
  preferred_notification_channel: "email" | "sms";
  default_address_id: number | null;
}

export interface AccountAddress {
  id: number;
  label: string | null;
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  longitude: number | null;
  latitude: number | null;
  default_delivery: boolean;
  updated_at: string;
}

export const profileKey = ["account", "profile"] as const;
export const addressesKey = ["account", "addresses"] as const;

export function useAccountProfile(): UseQueryResult<AccountProfile> {
  return useQuery({
    queryKey: profileKey,
    queryFn: () => fetchJson<{ profile: AccountProfile }>("/api/v1/account/me").then((r) => r.profile)
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AccountProfile>) =>
      fetchJson<{ profile: AccountProfile }>("/api/v1/account/profile", { method: "PATCH", body }).then(
        (r) => r.profile
      ),
    onSuccess: (profile) => {
      qc.setQueryData(profileKey, profile);
    }
  });
}

export function useUpdateEmail() {
  return useMutation({
    mutationFn: (body: { email: string; current_password: string }) =>
      fetchJson<{ user: { id: number; email: string; unconfirmed_email: string | null } }>(
        "/api/v1/account/email",
        { method: "PATCH", body }
      )
  });
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; password: string; password_confirmation: string }) =>
      fetchJson<unknown>("/api/v1/account/password", { method: "PATCH", body })
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => fetchJson<unknown>("/api/v1/account", { method: "DELETE" })
  });
}

export function useAddresses(): UseQueryResult<AccountAddress[]> {
  return useQuery({
    queryKey: addressesKey,
    queryFn: () =>
      fetchJson<{ addresses: AccountAddress[] }>("/api/v1/account/addresses").then((r) => r.addresses)
  });
}

export function useCreateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AccountAddress>) =>
      fetchJson<{ address: AccountAddress }>("/api/v1/account/addresses", {
        method: "POST",
        body: { address: body }
      }).then((r) => r.address),
    onSuccess: () => qc.invalidateQueries({ queryKey: addressesKey })
  });
}

export function useUpdateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<AccountAddress> & { id: number }) =>
      fetchJson<{ address: AccountAddress }>(`/api/v1/account/addresses/${id}`, {
        method: "PATCH",
        body: { address: body }
      }).then((r) => r.address),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addressesKey });
      qc.invalidateQueries({ queryKey: profileKey });
    }
  });
}

export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => fetchJson<unknown>(`/api/v1/account/addresses/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addressesKey });
      qc.invalidateQueries({ queryKey: profileKey });
    }
  });
}
